/**
 * ?? SAFE STORAGE - Utilitário para localStorage seguro
 * 
 * Previne crashes por:
 * - JSON corrompido
 * - localStorage cheio
 * - Dados inválidos
 * 
 * ?? OTIMIZADO: Reduzido para evitar custos de Firebase
 */

// Tamanho máximo por item (200KB para economizar espaço)
const MAX_ITEM_SIZE = 100 * 1024;

export const safeStorage = {
  /**
   * L� do localStorage com fallback seguro
   */
  get: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return defaultValue;
      
      const parsed = JSON.parse(item);
      
      // Valida��o b�sica de tipo
      if (Array.isArray(defaultValue) && !Array.isArray(parsed)) {
        console.warn(`?? Storage ${key}: esperava array, recebeu ${typeof parsed}`);
        return defaultValue;
      }
      
      return parsed;
    } catch (error) {
      console.error(`?? Erro ao ler ${key} do localStorage:`, error);
      // NÃO remover dados automaticamente - pode ser erro temporário
      console.warn(`?? Mantendo dados em ${key} - verifique manualmente se necessário`);
      return defaultValue;
    }
  },

  /**
   * Salva no localStorage com validação de tamanho
   * PRIORIZA não travar - se não couber, não salva
   * ?? ORDERS E VENDORS NÃO SÃO SALVOS - Firebase é fonte da verdade
   */
  set: <T>(key: string, value: T): boolean => {
    try {
      // ?? BLOQUEAR orders - Firebase é a ÚNICA fonte de verdade
      // Isso é OBRIGATÓRIO para evitar duplicação e inconsistência
      if (key.includes('order')) {
        console.warn(`?? [SafeStorage] BLOQUEADO: ${key} - Firebase é a ÚNICA fonte para orders`);
        return false; // NÃO salvar
      }
      
      // ?? BLOQUEAR vendors - muito grandes, causam loop
      if (key.includes('vendors')) {
        // Silenciosamente ignorar - Firebase é fonte da verdade
        return true;
      }
      
      const serialized = JSON.stringify(value);
      
      // Se item muito grande, limitar dados
      if (serialized.length > MAX_ITEM_SIZE) {
        // ?? Log apenas 1x por minuto para não poluir console
        const lastWarn = (window as any).__lastStorageWarn || 0;
        if (Date.now() - lastWarn > 60000) {
          console.warn(`?? ${key} muito grande (${(serialized.length / 1024).toFixed(0)}KB) - usando Firebase`);
          (window as any).__lastStorageWarn = Date.now();
        }
        
        // Se for array, manter apenas os mais recentes
        if (Array.isArray(value)) {
          const limited = (value as any[]).slice(-20); // ?? Reduzido de 50 para 20
          const limitedSerialized = JSON.stringify(limited);
          if (limitedSerialized.length <= MAX_ITEM_SIZE) {
            localStorage.setItem(key, limitedSerialized);
            return true;
          }
        }
        
        // Se ainda muito grande, não salvar
        return false;
      }
      
      localStorage.setItem(key, serialized);
      return true;
    } catch (error) {
      // ?? Log silencioso - não poluir console
      
      // Se localStorage cheio, limpar e tentar novamente
      if (error instanceof DOMException && 
          (error.name === 'QuotaExceededError' || error.code === 22)) {
        safeStorage.clearAll();
        
        // Tentar novamente com dados limitados
        try {
          if (Array.isArray(value)) {
            const limited = (value as any[]).slice(-20); // Bem limitado
            localStorage.setItem(key, JSON.stringify(limited));
            console.log(`? ${key} salvo após limpeza (${limited.length} itens)`);
            return true;
          }
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch {
          console.warn(`?? ${key} não salvo - Firebase é a fonte da verdade`);
          return false;
        }
      }
      return false;
    }
  },

  /**
   * Remove item com seguran�a
   */
  remove: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Limpa TODOS os dados do E-Ambulante para liberar espa�o
   */
  clearAll: () => {
    try {
      const keysToRemove = [
        'eambulante_orders_v4',
        'eambulante_products_v4', 
        'eambulante_vendors_v4',
        'eambulante_drivers_v4',
        'eambulante_clients_v4',
        'eambulante_messages_v4',
        'eambulante_errors',
        'eambulante_sync_lock',
        'eambulante_timestamp'
      ];
      
      keysToRemove.forEach(key => {
        try { localStorage.removeItem(key); } catch {}
      });
      
      console.log('?? localStorage limpo - dados serão recarregados do Firebase');
    } catch (error) {
      console.error('Erro ao limpar localStorage:', error);
    }
  },

  /**
   * Limpa dados antigos para liberar espa�o
   */
  cleanOldData: () => {
    try {
      // Remove pedidos com mais de 7 dias (reduzido de 30)
      const orders = safeStorage.get<any[]>('eambulante_orders_v4', []);
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const recentOrders = orders.filter(o => {
        const timestamp = o.timestamp ? new Date(o.timestamp).getTime() : 0;
        return timestamp > sevenDaysAgo;
      }).slice(-30); // M�ximo 30 pedidos
      
      if (recentOrders.length < orders.length) {
        console.log(`?? Limpando ${orders.length - recentOrders.length} pedidos antigos`);
        try {
          localStorage.setItem('eambulante_orders_v4', JSON.stringify(recentOrders));
        } catch {
          localStorage.removeItem('eambulante_orders_v4');
        }
      }
      
      // Limpar products para apenas os do vendedor atual (se houver)
      // Products s�o melhor gerenciados pelo Firebase
      const products = safeStorage.get<any[]>('eambulante_products_v4', []);
      if (products.length > 100) {
        console.log(`?? Muitos produtos (${products.length}), removendo cache local`);
        localStorage.removeItem('eambulante_products_v4');
      }
      
      // Remove erros antigos
      localStorage.removeItem('eambulante_errors');
      
      // Remove mensagens antigas - manter s� �ltimas 20
      const messages = safeStorage.get<any[]>('eambulante_messages_v4', []);
      if (messages.length > 20) {
        const recentMessages = messages.slice(-20);
        try {
          localStorage.setItem('eambulante_messages_v4', JSON.stringify(recentMessages));
        } catch {
          localStorage.removeItem('eambulante_messages_v4');
        }
      }
      
      console.log('? Limpeza de dados antigos concluída');
    } catch (error) {
      console.error('Erro ao limpar dados antigos:', error);
      // Em caso de erro, limpar tudo
      safeStorage.clearAll();
    }
  },

  /**
   * Retorna uso atual do localStorage
   */
  getUsage: (): { used: number; total: number; percent: number } => {
    try {
      let total = 0;
      for (const key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          total += (localStorage.getItem(key)?.length || 0) * 2; // UTF-16
        }
      }
      const maxSize = 5 * 1024 * 1024; // 5MB t�pico
      return {
        used: total,
        total: maxSize,
        percent: Math.round((total / maxSize) * 100)
      };
    } catch {
      return { used: 0, total: 5 * 1024 * 1024, percent: 0 };
    }
  },

  /**
   * Verifica se localStorage est� dispon�vel
   */
  isAvailable: (): boolean => {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }
};
